import {
  Button,
  Flex,
  HelpIndicator,
  Link,
  ModalV2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Text,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { mapCaseTypeToText } from "~/pages/Compliance/ComplianceTable/CaseTypeRenderer/utils";
import { mapStatusToText } from "~/pages/Compliance/ComplianceTable/utils";
import { toStandardFormat } from "~/utils/datetime";
import { getInvestingProfileName } from "../../Profiles/utils";
import * as styles from "./styles_vanilla.css";
import { type RelatedCase } from "./utils";

export type RelatedCasesModalProps = {
  show: boolean;
  onHide: () => void;
  relatedCases: RelatedCase[];
};

const buildCaseUrl = (caseId: number) => `${globalEnv.complianceRoute}/case/${caseId}`;

const buildViewAllUrl = (cases: RelatedCase[]) => {
  const ids = cases.map((c) => c.id).join(",");
  return `${globalEnv.complianceRoute}?caseIds=${ids}`;
};

const StatusCell = ({ status }: { status: RelatedCase["status"] }) => {
  const mapped = mapStatusToText(status);
  return (
    <Flex alignItems="center" gap={8}>
      {mapped.iconType && <Icon type={mapped.iconType} />}
      <Text>{mapped.displayText}</Text>
    </Flex>
  );
};

export const RelatedCasesModal = ({ show, onHide, relatedCases }: RelatedCasesModalProps) => (
  <ModalV2
    show={show}
    onHide={onHide}
    title="Current open cases"
    size="xl"
    data-testid="related-cases-modal"
  >
    <ModalV2.Body>
      <Text variant="regular-md" className={styles.modalIntro}>
        This shows the open cases currently using the shared profile. Modifications made to the
        shared profile may affect the other open cases.
      </Text>
      <div className={styles.tableWrapper}>
        <Table condensed hasBottomBorder>
          <TableHeader>
            <TableRow className={styles.headerRow}>
              <TableCell header className={styles.cellWithRightBorder}>
                Case type
              </TableCell>
              <TableCell header>Case status</TableCell>
              <TableCell header>Case creation date</TableCell>
              <TableCell header className={styles.cellWithRightBorder}>
                Shared profile
              </TableCell>
              <TableCell header className={styles.actionsCell} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {relatedCases.map((relatedCase) => (
              <TableRow key={relatedCase.id} className={styles.bodyRow}>
                <TableCell className={styles.cellWithRightBorder}>
                  {mapCaseTypeToText(relatedCase.caseType as ComplianceCaseTypeEnum)}
                </TableCell>
                <TableCell>
                  <StatusCell status={relatedCase.status} />
                </TableCell>
                <TableCell>
                  {relatedCase.createdAt
                    ? toStandardFormat(relatedCase.createdAt, "MMM d, yyyy")
                    : ""}
                </TableCell>
                <TableCell className={styles.cellWithRightBorder}>
                  {getInvestingProfileName(relatedCase.investingProfile)}
                </TableCell>
                <TableCell className={styles.actionsCell}>
                  <HelpIndicator
                    id={`related-case-link-tooltip-${relatedCase.id}`}
                    placement="top"
                    content="View case"
                  >
                    <Link
                      to={buildCaseUrl(relatedCase.id)}
                      shouldOpenInNewTab
                      aria-label="Open case in new tab"
                      data-testid={`related-case-link-${relatedCase.id}`}
                    >
                      <Icon type="external-link" color={Colors.Primary600} variant="outline" />
                    </Link>
                  </HelpIndicator>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </ModalV2.Body>
    <ModalV2.Footer>
      <Flex className={styles.footerRow} alignItems="center" justifyContent="flex-end" gap={12}>
        <Link
          to={buildViewAllUrl(relatedCases)}
          shouldOpenInNewTab
          className={styles.viewAllLink}
          data-testid="view-all-related-cases-link"
        >
          View all related cases
          <Icon type="external-link" color={Colors.Primary600} variant="outline" />
        </Link>
        <Button variant="neutral" onClick={onHide} data-testid="related-cases-modal-close">
          Close
        </Button>
      </Flex>
    </ModalV2.Footer>
  </ModalV2>
);
